import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Snackbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Warning as WarningIcon,
  LocalHospital as LocalHospitalIcon,
  Medication as MedicationIcon,
  Science as ScienceIcon,
  Person as PersonIcon,
  FamilyRestroom as FamilyIcon,
  Build as BuildIcon,
  History as HistoryIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Print as PrintIcon,
  AutoAwesome as AIIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  getCaseSummary,
  generateCaseSummary,
  generateAICaseSummary,
  updateCaseSummary,
  needsRefresh,
  getStatusColor,
} from '../../services/caseSummaryService';
import '../../styles/caseSummary.css';

// Styled components
const SectionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  '& .MuiCardHeader-root': {
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1, 2),
  },
  '& .MuiCardContent-root': {
    padding: theme.spacing(2),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.95rem',
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const RiskFlagChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.error.light,
  color: theme.palette.error.contrastText,
  fontWeight: 600,
  margin: theme.spacing(0.5),
}));

const KeyDiagnosisChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.warning.light,
  color: theme.palette.warning.contrastText,
  fontWeight: 600,
  margin: theme.spacing(0.5),
}));

const NarrativeBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  lineHeight: 1.6,
}));

const CaseSummaryTab = ({ patient, facilityId }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedSummary, setEditedSummary] = useState({});
  const [expandedSections, setExpandedSections] = useState(new Set([
    'narrative', 'riskFlags', 'activeProblems', 'medications', 'allergies'
  ]));
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);

  // Load case summary on mount
  const loadCaseSummary = useCallback(async () => {
    if (!patient?.id || !facilityId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await getCaseSummary(patient.id, facilityId);
      setSummary(response.summary);
    } catch (err) {
      console.error('Error loading case summary:', err);
      setError('Failed to load case summary. Click "Generate Summary" to create one.');
    } finally {
      setLoading(false);
    }
  }, [patient?.id, facilityId]);

  useEffect(() => {
    loadCaseSummary();
  }, [loadCaseSummary]);

  // Generate or regenerate case summary
  const handleGenerateSummary = async (forceRegenerate = false) => {
    if (!patient?.id || !facilityId) return;

    setGenerating(true);
    setError(null);
    try {
      const response = await generateCaseSummary(patient.id, facilityId, forceRegenerate);
      setSummary(response.summary);
      setSnackbar({
        open: true,
        message: response.isNew ? 'Case summary generated successfully!' : 'Using existing case summary.',
        severity: 'success',
      });
    } catch (err) {
      console.error('Error generating case summary:', err);
      setError('Failed to generate case summary. Please try again.');
      setSnackbar({
        open: true,
        message: 'Failed to generate case summary.',
        severity: 'error',
      });
    } finally {
      setGenerating(false);
    }
  };

  // Generate AI-enhanced summary content
  const handleGenerateAISummary = async () => {
    if (!patient?.id || !facilityId) return;

    setAiGenerating(true);
    try {
      const response = await generateAICaseSummary(patient.id, facilityId);
      
      if (response.generatedSummary) {
        // Update the current summary with AI-generated content
        const aiContent = response.generatedSummary;
        const updatedSummary = {
          ...summary,
          historyOfPresentIllness: aiContent.historyOfPresentIllness || summary?.historyOfPresentIllness,
          assessment: aiContent.assessment || summary?.assessment,
          plan: aiContent.plan || summary?.plan || [],
          riskFlags: aiContent.riskFlags || summary?.riskFlags || [],
          keyDiagnoses: aiContent.keyDiagnoses || summary?.keyDiagnoses || [],
          medTrends: aiContent.medTrends || summary?.medTrends || [],
          narrativeSummary: aiContent.narrativeSummary || summary?.narrativeSummary,
        };

        // Save the AI-enhanced content
        if (summary?.id) {
          await updateCaseSummary(patient.id, facilityId, updatedSummary);
        }

        setSummary(updatedSummary);
        setSnackbar({
          open: true,
          message: 'AI-generated content added successfully!',
          severity: 'success',
        });
      }
    } catch (err) {
      console.error('Error generating AI summary:', err);
      setSnackbar({
        open: true,
        message: 'Failed to generate AI content. Please try again.',
        severity: 'error',
      });
    } finally {
      setAiGenerating(false);
    }
  };

  // Save edited summary
  const handleSaveEdit = async () => {
    if (!patient?.id || !facilityId) return;

    try {
      const response = await updateCaseSummary(patient.id, facilityId, editedSummary);
      setSummary(response.summary);
      setEditMode(false);
      setSnackbar({
        open: true,
        message: 'Case summary updated successfully!',
        severity: 'success',
      });
    } catch (err) {
      console.error('Error updating case summary:', err);
      setSnackbar({
        open: true,
        message: 'Failed to update case summary.',
        severity: 'error',
      });
    }
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  // Enter edit mode
  const handleStartEdit = () => {
    setEditedSummary({ ...summary });
    setEditMode(true);
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditedSummary({});
    setEditMode(false);
  };

  // Update edited field
  const handleEditField = (field, value) => {
    setEditedSummary(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Format date for display
  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return format(date, 'MMM dd, yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  // Render header section
  const renderHeader = () => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {summary && (
            <Chip
              label={summary.status || 'active'}
              color={getStatusColor(summary.status)}
              size="small"
            />
          )}
          {summary?.needsRefresh && (
            <Chip
              label="Needs Refresh"
              color="warning"
              size="small"
              icon={<WarningIcon />}
            />
          )}
        </Box>
      </Box>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          onClick={() => handleGenerateSummary(true)}
          disabled={generating || loading}
          size="small"
        >
          {generating ? 'Generating...' : (summary ? 'Refresh Summary' : 'Generate Summary')}
        </Button>
        
        {summary && (
          <>
            <Button
              variant="outlined"
              startIcon={aiGenerating ? <CircularProgress size={16} /> : <AIIcon />}
              onClick={handleGenerateAISummary}
              disabled={aiGenerating || generating}
              size="small"
            >
              {aiGenerating ? 'Generating AI Content...' : 'Enhance with AI'}
            </Button>
            
            {editMode ? (
              <>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveEdit}
                  size="small"
                >
                  Save Changes
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelEdit}
                  size="small"
                >
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleStartEdit}
                size="small"
              >
                Edit Summary
              </Button>
            )}
            
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => setHistoryDialogOpen(true)}
              size="small"
            >
              History
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
              size="small"
            >
              Print
            </Button>
          </>
        )}
      </Box>

      {/* Last updated info */}
      {summary && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Last updated: {formatDateTime(summary.updatedAt)}
          {(summary.updatedByName || summary.lastManualEditByName) && ` by ${summary.lastManualEditByName || summary.updatedByName}`}
          {summary.isAiGenerated && ' • AI Generated'}
        </Typography>
      )}
    </Box>
  );

  // Render risk flags and key diagnoses
  const renderRiskFlagsAndDiagnoses = () => {
    const riskFlags = summary?.riskFlags || [];
    const keyDiagnoses = summary?.keyDiagnoses || [];
    
    if (riskFlags.length === 0 && keyDiagnoses.length === 0) return null;

    return (
      <Paper sx={{ p: 2, mb: 2, backgroundColor: 'rgba(255, 152, 0, 0.05)' }}>
        <Grid container spacing={2}>
          {riskFlags.length > 0 && (
            <Grid item xs={12} md={6}>
              <SectionTitle>
                <WarningIcon color="error" />
                Risk Flags
              </SectionTitle>
              <Box sx={{ mt: 1 }}>
                {riskFlags.map((flag, index) => (
                  <RiskFlagChip key={index} label={flag} size="small" />
                ))}
              </Box>
            </Grid>
          )}
          {keyDiagnoses.length > 0 && (
            <Grid item xs={12} md={6}>
              <SectionTitle>
                <AssessmentIcon color="warning" />
                Key Diagnoses
              </SectionTitle>
              <Box sx={{ mt: 1 }}>
                {keyDiagnoses.map((diagnosis, index) => (
                  <KeyDiagnosisChip key={index} label={diagnosis} size="small" />
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>
    );
  };

  // Render narrative summary
  const renderNarrativeSummary = () => {
    const content = editMode ? editedSummary.narrativeSummary : summary?.narrativeSummary;
    
    return (
      <SectionCard>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <SectionTitle>
                <AssessmentIcon />
                Executive Summary
              </SectionTitle>
              <IconButton size="small" onClick={() => toggleSection('narrative')}>
                {expandedSections.has('narrative') ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          }
        />
        <Collapse in={expandedSections.has('narrative')}>
          <CardContent>
            {editMode ? (
              <TextField
                fullWidth
                multiline
                rows={4}
                value={content || ''}
                onChange={(e) => handleEditField('narrativeSummary', e.target.value)}
                placeholder="Enter executive summary..."
              />
            ) : (
              <NarrativeBox>
                {content || 'No executive summary available. Click "Enhance with AI" to generate one.'}
              </NarrativeBox>
            )}
          </CardContent>
        </Collapse>
      </SectionCard>
    );
  };

  // Render HPI section
  const renderHPI = () => {
    const content = editMode ? editedSummary.historyOfPresentIllness : summary?.historyOfPresentIllness;
    
    return (
      <SectionCard>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <SectionTitle>
                <HistoryIcon />
                History of Present Illness
              </SectionTitle>
              <IconButton size="small" onClick={() => toggleSection('hpi')}>
                {expandedSections.has('hpi') ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          }
        />
        <Collapse in={expandedSections.has('hpi')}>
          <CardContent>
            {editMode ? (
              <TextField
                fullWidth
                multiline
                rows={6}
                value={content || ''}
                onChange={(e) => handleEditField('historyOfPresentIllness', e.target.value)}
                placeholder="Enter history of present illness..."
              />
            ) : (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {content || 'Not documented'}
              </Typography>
            )}
          </CardContent>
        </Collapse>
      </SectionCard>
    );
  };

  // Render active problems section
  const renderActiveProblems = () => {
    const problems = summary?.activeProblems || summary?.keyDiagnoses || [];
    const pmh = summary?.pastMedicalHistory || [];
    
    return (
      <SectionCard>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <SectionTitle>
                <LocalHospitalIcon />
                Active Problems & Medical History
              </SectionTitle>
              <IconButton size="small" onClick={() => toggleSection('activeProblems')}>
                {expandedSections.has('activeProblems') ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          }
        />
        <Collapse in={expandedSections.has('activeProblems')}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Active Problems</Typography>
                {problems.length > 0 ? (
                  <List dense>
                    {problems.map((problem, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText primary={problem} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">None documented</Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Past Medical History</Typography>
                {pmh.length > 0 ? (
                  <List dense>
                    {pmh.slice(0, 10).map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                    {pmh.length > 10 && (
                      <Typography variant="caption" color="text.secondary">
                        +{pmh.length - 10} more...
                      </Typography>
                    )}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">None documented</Typography>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </SectionCard>
    );
  };

  // Render medications section
  const renderMedications = () => {
    const medications = summary?.medications || [];
    const medTrends = summary?.medTrends || [];
    
    return (
      <SectionCard>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <SectionTitle>
                <MedicationIcon />
                Current Medications
                {medications.length > 0 && (
                  <Chip label={medications.length} size="small" sx={{ ml: 1 }} />
                )}
              </SectionTitle>
              <IconButton size="small" onClick={() => toggleSection('medications')}>
                {expandedSections.has('medications') ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          }
        />
        <Collapse in={expandedSections.has('medications')}>
          <CardContent>
            {medTrends.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Medication Trends:</strong> {medTrends.join(' • ')}
              </Alert>
            )}
            {medications.length > 0 ? (
              <Grid container spacing={1}>
                {medications.map((med, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {med.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {med.dosage} {med.frequency && `• ${med.frequency}`} {med.route && `• ${med.route}`}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">No medications documented</Typography>
            )}
          </CardContent>
        </Collapse>
      </SectionCard>
    );
  };

  // Render allergies section
  const renderAllergies = () => {
    const allergies = summary?.allergies || [];
    
    return (
      <SectionCard>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <SectionTitle>
                <WarningIcon color="error" />
                Allergies
                {allergies.length > 0 && (
                  <Chip label={allergies.length} size="small" color="error" sx={{ ml: 1 }} />
                )}
              </SectionTitle>
              <IconButton size="small" onClick={() => toggleSection('allergies')}>
                {expandedSections.has('allergies') ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          }
        />
        <Collapse in={expandedSections.has('allergies')}>
          <CardContent>
            {allergies.length > 0 ? (
              <List dense>
                {allergies.map((allergy, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <WarningIcon color={allergy.severity === 'severe' || allergy.severity === 'life-threatening' ? 'error' : 'warning'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={allergy.allergen}
                      secondary={`${allergy.reaction || 'Unknown reaction'}${allergy.severity ? ` • ${allergy.severity}` : ''}`}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                NKDA (No Known Drug Allergies)
              </Typography>
            )}
          </CardContent>
        </Collapse>
      </SectionCard>
    );
  };

  // Render surgical history section
  const renderSurgicalHistory = () => {
    const surgicalHistory = summary?.surgicalHistory || [];
    
    return (
      <SectionCard>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <SectionTitle>
                <BuildIcon />
                Surgical History
              </SectionTitle>
              <IconButton size="small" onClick={() => toggleSection('surgical')}>
                {expandedSections.has('surgical') ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          }
        />
        <Collapse in={expandedSections.has('surgical')}>
          <CardContent>
            {surgicalHistory.length > 0 ? (
              <List dense>
                {surgicalHistory.map((surgery, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={surgery} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">No surgical history documented</Typography>
            )}
          </CardContent>
        </Collapse>
      </SectionCard>
    );
  };

  // Render social history section
  const renderSocialHistory = () => {
    const social = summary?.socialHistory || {};
    
    return (
      <SectionCard>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <SectionTitle>
                <PersonIcon />
                Social History
              </SectionTitle>
              <IconButton size="small" onClick={() => toggleSection('social')}>
                {expandedSections.has('social') ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          }
        />
        <Collapse in={expandedSections.has('social')}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Smoking</Typography>
                <Typography variant="body2">
                  {social.smoking?.status || 'Unknown'}
                  {social.smoking?.details && ` - ${social.smoking.details}`}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Alcohol</Typography>
                <Typography variant="body2">
                  {social.alcohol?.status || 'Unknown'}
                  {social.alcohol?.details && ` - ${social.alcohol.details}`}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" color="text.secondary">Occupation</Typography>
                <Typography variant="body2">{social.occupation || 'Not documented'}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </SectionCard>
    );
  };

  // Render family history section
  const renderFamilyHistory = () => {
    const familyHistory = summary?.familyHistory || [];
    
    return (
      <SectionCard>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <SectionTitle>
                <FamilyIcon />
                Family History
              </SectionTitle>
              <IconButton size="small" onClick={() => toggleSection('family')}>
                {expandedSections.has('family') ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          }
        />
        <Collapse in={expandedSections.has('family')}>
          <CardContent>
            {familyHistory.length > 0 ? (
              <List dense>
                {familyHistory.map((item, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">Non-contributory</Typography>
            )}
          </CardContent>
        </Collapse>
      </SectionCard>
    );
  };

  // Render diagnostics section
  const renderDiagnostics = () => {
    const diagnostics = summary?.diagnostics || [];
    
    return (
      <SectionCard>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <SectionTitle>
                <ScienceIcon />
                Recent Lab Results
              </SectionTitle>
              <IconButton size="small" onClick={() => toggleSection('diagnostics')}>
                {expandedSections.has('diagnostics') ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          }
        />
        <Collapse in={expandedSections.has('diagnostics')}>
          <CardContent>
            {diagnostics.length > 0 ? (
              <Grid container spacing={1}>
                {diagnostics.slice(0, 10).map((result, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 1.5,
                        borderColor: result.abnormal ? 'error.main' : 'divider',
                        backgroundColor: result.abnormal ? 'error.light' : 'transparent',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {result.name}
                        {result.abnormal && (
                          <Chip label="Abnormal" size="small" color="error" sx={{ ml: 1 }} />
                        )}
                      </Typography>
                      <Typography variant="body2">{result.result}</Typography>
                      <Typography variant="caption" color="text.secondary">{result.date}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">No recent lab results</Typography>
            )}
          </CardContent>
        </Collapse>
      </SectionCard>
    );
  };

  // Render assessment and plan section
  const renderAssessmentPlan = () => {
    const assessment = editMode ? editedSummary.assessment : summary?.assessment;
    const plan = editMode ? editedSummary.plan : summary?.plan || [];
    
    return (
      <SectionCard>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <SectionTitle>
                <AssessmentIcon />
                Assessment & Plan
              </SectionTitle>
              <IconButton size="small" onClick={() => toggleSection('assessmentPlan')}>
                {expandedSections.has('assessmentPlan') ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          }
        />
        <Collapse in={expandedSections.has('assessmentPlan')}>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Assessment</Typography>
                {editMode ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={assessment || ''}
                    onChange={(e) => handleEditField('assessment', e.target.value)}
                    placeholder="Enter clinical assessment..."
                  />
                ) : (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {assessment || 'Not documented'}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Plan</Typography>
                {plan.length > 0 ? (
                  <List dense>
                    {plan.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{index + 1}.</Typography>
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">No active plan items</Typography>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </SectionCard>
    );
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading case summary...</Typography>
      </Box>
    );
  }

  // No summary state
  if (!summary && !error) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <AssessmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom>No Case Summary Available</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Generate a longitudinal case summary to get a comprehensive clinical snapshot of this patient.
        </Typography>
        <Button
          variant="contained"
          startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          onClick={() => handleGenerateSummary(false)}
          disabled={generating}
        >
          {generating ? 'Generating...' : 'Generate Case Summary'}
        </Button>
      </Box>
    );
  }

  return (
    <Box className="case-summary-container">
      {renderHeader()}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {summary?.needsRefresh && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Summary needs refresh:</strong> {summary.refreshReason || 'Clinical data has changed since last update.'}
        </Alert>
      )}

      {summary && (
        <>
          {renderRiskFlagsAndDiagnoses()}
          {renderNarrativeSummary()}
          {renderHPI()}
          {renderActiveProblems()}
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {renderMedications()}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderAllergies()}
            </Grid>
          </Grid>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {renderSurgicalHistory()}
            </Grid>
            <Grid item xs={12} md={6}>
              {renderFamilyHistory()}
            </Grid>
          </Grid>
          
          {renderSocialHistory()}
          {renderDiagnostics()}
          {renderAssessmentPlan()}
        </>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* History Dialog */}
      <Dialog 
        open={historyDialogOpen} 
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Case Summary History</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Version history feature coming soon. This will show all previous versions of the case summary.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CaseSummaryTab;
